import type { CSSProperties } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { gsap } from "gsap";
 
type Dot = {
  cx: number;
  cy: number;
  xOffset: number;
  yOffset: number;
  _animating: boolean;
};
 
export type DotGridProps = {
  dotSize?: number;
  gap?: number;
  baseColor?: string;
  activeColor?: string;
  proximity?: number;
  speedTrigger?: number;
  shockRadius?: number;
  shockStrength?: number;
  maxSpeed?: number;
  resistance?: number;
  returnDuration?: number;
  className?: string;
  style?: CSSProperties;
};
 
function throttle<TArgs extends any[]>(fn: (...args: TArgs) => void, limitMs: number) {
  let lastCall = 0;
  return (...args: TArgs) => {
    const now = performance.now();
    if (now - lastCall >= limitMs) {
      lastCall = now;
      fn(...args);
    }
  };
}
 
function parseRgba(color: string): { r: number; g: number; b: number; a: number } | null {
  // #RRGGBB
  const hex = color.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (hex) {
    return {
      r: parseInt(hex[1]!, 16),
      g: parseInt(hex[2]!, 16),
      b: parseInt(hex[3]!, 16),
      a: 1,
    };
  }
 
  // rgb(...) / rgba(...) (accept both comma and space separated forms after computed styles)
  const rgb = color
    .replace(/\s+/g, " ")
    .match(/^rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s/]+([\d.]+))?\)$/i);
  if (rgb) {
    return {
      r: Number(rgb[1]),
      g: Number(rgb[2]),
      b: Number(rgb[3]),
      a: rgb[4] == null ? 1 : Number(rgb[4]),
    };
  }
 
  return null;
}
 
function resolveColorToRgba(color: string): { r: number; g: number; b: number; a: number } {
  const direct = parseRgba(color);
  if (direct) return direct;
 
  // Fallback: resolve via browser computed styles.
  if (typeof window === "undefined" || typeof document === "undefined") {
    return { r: 0, g: 0, b: 0, a: 1 };
  }
 
  const el = document.createElement("span");
  el.style.color = color;
  el.style.display = "none";
  document.body.appendChild(el);
  const computed = getComputedStyle(el).color;
  document.body.removeChild(el);
  return parseRgba(computed) ?? { r: 0, g: 0, b: 0, a: 1 };
}
 
export function DotGrid({
  dotSize = 6,
  gap = 16,
  baseColor = "#D1D5DB",
  activeColor = "#111827",
  proximity = 140,
  speedTrigger = 900,
  shockRadius = 220,
  shockStrength = 0.18,
  maxSpeed = 6000,
  resistance = 850,
  returnDuration = 1.6,
  className = "",
  style,
}: DotGridProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dotsRef = useRef<Dot[]>([]);
  const sizeRef = useRef({ width: 0, height: 0 });
  const pointerRef = useRef({
    x: -99999,
    y: -99999,
    vx: 0,
    vy: 0,
    speed: 0,
    lastTime: 0,
    lastX: 0,
    lastY: 0,
  });
 
  const baseRgba = useMemo(() => resolveColorToRgba(baseColor), [baseColor]);
  const activeRgba = useMemo(() => resolveColorToRgba(activeColor), [activeColor]);
 
  const circlePath = useMemo(() => {
    if (typeof Path2D === "undefined") return null;
    const p = new Path2D();
    p.arc(0, 0, dotSize / 2, 0, Math.PI * 2);
    return p;
  }, [dotSize]);
 
  const buildGrid = useCallback(() => {
    const wrap = wrapperRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
 
    const rect = wrap.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width));
    const height = Math.max(1, Math.floor(rect.height));
    sizeRef.current = { width, height };
 
    const dpr =
      typeof globalThis !== "undefined" && "devicePixelRatio" in globalThis
        ? ((globalThis as unknown as Window).devicePixelRatio || 1)
        : 1;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
 
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
 
    const cols = Math.floor((width + gap) / (dotSize + gap));
    const rows = Math.floor((height + gap) / (dotSize + gap));
    const cell = dotSize + gap;
 
    const gridW = cell * cols - gap;
    const gridH = cell * rows - gap;
 
    const extraX = width - gridW;
    const extraY = height - gridH;
 
    const startX = extraX / 2 + dotSize / 2;
    const startY = extraY / 2 + dotSize / 2;
 
    const dots: Dot[] = [];
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        dots.push({
          cx: startX + x * cell,
          cy: startY + y * cell,
          xOffset: 0,
          yOffset: 0,
          _animating: false,
        });
      }
    }
    dotsRef.current = dots;
  }, [dotSize, gap]);
 
  useEffect(() => {
    buildGrid();
    let ro: ResizeObserver | null = null;
    const win =
      typeof document !== "undefined" ? (globalThis as unknown as Window) : null;

    if (win && "ResizeObserver" in win) {
      ro = new ResizeObserver(buildGrid);
      if (wrapperRef.current) ro.observe(wrapperRef.current);
    } else if (win) {
      win.addEventListener("resize", buildGrid);
    }
    return () => {
      if (ro) ro.disconnect();
      else win?.removeEventListener("resize", buildGrid);
    };
  }, [buildGrid]);
 
  useEffect(() => {
    if (!circlePath) return;
 
    let rafId = 0;
    const proxSq = proximity * proximity;
 
    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
 
      const { width, height } = sizeRef.current;
      ctx.clearRect(0, 0, width, height);
 
      const { x: px, y: py } = pointerRef.current;
      for (const dot of dotsRef.current) {
        const ox = dot.cx + dot.xOffset;
        const oy = dot.cy + dot.yOffset;
        const dx = dot.cx - px;
        const dy = dot.cy - py;
        const dsq = dx * dx + dy * dy;
 
        let fill = baseColor;
        if (dsq <= proxSq) {
          const dist = Math.sqrt(dsq);
          const t = 1 - dist / proximity;
          const r = Math.round(baseRgba.r + (activeRgba.r - baseRgba.r) * t);
          const g = Math.round(baseRgba.g + (activeRgba.g - baseRgba.g) * t);
          const b = Math.round(baseRgba.b + (activeRgba.b - baseRgba.b) * t);
          const a = baseRgba.a + (activeRgba.a - baseRgba.a) * t;
          fill = `rgba(${r},${g},${b},${Math.max(0, Math.min(1, a))})`;
        }
 
        ctx.save();
        ctx.translate(ox, oy);
        ctx.fillStyle = fill;
        ctx.fill(circlePath);
        ctx.restore();
      }
 
      rafId = (globalThis as unknown as Window).requestAnimationFrame(draw);
    };
 
    rafId = (globalThis as unknown as Window).requestAnimationFrame(draw);
    return () => (globalThis as unknown as Window).cancelAnimationFrame(rafId);
  }, [circlePath, proximity, baseColor, baseRgba, activeRgba, activeColor]);
 
  useEffect(() => {
    const win =
      typeof document !== "undefined" ? (globalThis as unknown as Window) : null;
    const prefersReducedMotion =
      !!win?.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
 
    const kick = (dot: Dot, pushX: number, pushY: number) => {
      dot._animating = true;
      gsap.killTweensOf(dot);
 
      // Resistance is treated as "how quickly it settles"; larger = smaller movement.
      const movementScale = Math.max(0.12, Math.min(1, 750 / Math.max(1, resistance)));
      const targetX = pushX * movementScale;
      const targetY = pushY * movementScale;
 
      gsap.to(dot, {
        xOffset: targetX,
        yOffset: targetY,
        duration: 0.55,
        ease: "power3.out",
        onComplete: () => {
          gsap.to(dot, {
            xOffset: 0,
            yOffset: 0,
            duration: returnDuration,
            ease: "elastic.out(1,0.75)",
            onComplete: () => {
              dot._animating = false;
            },
          });
        },
      });
    };
 
    const onMove = (e: PointerEvent) => {
      const wrap = wrapperRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;
      if (!inside) {
        pointerRef.current.x = -99999;
        pointerRef.current.y = -99999;
        return;
      }
 
      const now = performance.now();
      const pr = pointerRef.current;
      const dt = pr.lastTime ? now - pr.lastTime : 16;
      const dx = e.clientX - pr.lastX;
      const dy = e.clientY - pr.lastY;
 
      let vx = (dx / dt) * 1000;
      let vy = (dy / dt) * 1000;
      let speed = Math.hypot(vx, vy);
      if (speed > maxSpeed) {
        const scale = maxSpeed / speed;
        vx *= scale;
        vy *= scale;
        speed = maxSpeed;
      }
 
      pr.lastTime = now;
      pr.lastX = e.clientX;
      pr.lastY = e.clientY;
      pr.vx = vx;
      pr.vy = vy;
      pr.speed = speed;
      pr.x = e.clientX - rect.left;
      pr.y = e.clientY - rect.top;
 
      if (prefersReducedMotion) return;
 
      // Fast swipes gently "kick" nearby dots.
      if (speed <= speedTrigger) return;
      for (const dot of dotsRef.current) {
        const dist = Math.hypot(dot.cx - pr.x, dot.cy - pr.y);
        if (dist < proximity && !dot._animating) {
          const falloff = Math.max(0, 1 - dist / proximity);
          const pushX = (dot.cx - pr.x + vx * 0.004) * falloff;
          const pushY = (dot.cy - pr.y + vy * 0.004) * falloff;
          kick(dot, pushX, pushY);
        }
      }
    };
 
    const onClick = (e: PointerEvent) => {
      const wrap = wrapperRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;
      if (!inside || prefersReducedMotion) return;
 
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      for (const dot of dotsRef.current) {
        const dist = Math.hypot(dot.cx - cx, dot.cy - cy);
        if (dist < shockRadius && !dot._animating) {
          const falloff = Math.max(0, 1 - dist / shockRadius);
          const pushX = (dot.cx - cx) * shockStrength * falloff;
          const pushY = (dot.cy - cy) * shockStrength * falloff;
          kick(dot, pushX, pushY);
        }
      }
    };
 
    const throttledMove = throttle(onMove, 50);
    win?.addEventListener("pointermove", throttledMove, { passive: true });
    win?.addEventListener("pointerdown", onClick, { passive: true });
 
    return () => {
      win?.removeEventListener("pointermove", throttledMove as any);
      win?.removeEventListener("pointerdown", onClick);
    };
  }, [
    maxSpeed,
    speedTrigger,
    proximity,
    resistance,
    returnDuration,
    shockRadius,
    shockStrength,
  ]);
 
  return (
    <div
      ref={wrapperRef}
      className={["relative h-full w-full", className].filter(Boolean).join(" ")}
      style={style}
    >
      <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" />
    </div>
  );
}
 
export default DotGrid;
